/**
 * safeStorage — a thin wrapper around localStorage that:
 *   • Namespaces every key under `ago.` so we never collide with other apps.
 *   • Catches QuotaExceeded / disabled-storage / private-mode errors so a
 *     missing localStorage never crashes the app.
 *   • JSON-encodes/decodes objects automatically.
 *   • REFUSES to ever store auth tokens / passwords / credentials. Server
 *     issues HttpOnly cookies for those; nothing sensitive should ever
 *     land in window.localStorage.
 *
 * Note: the keys we store today are all UI state (onboarding completion,
 * sidebar-collapsed, tour-seen flags). No secrets ever go through here.
 */
const FORBIDDEN_KEYS = /token|password|secret|api[_-]?key|credential|session/i;
const NAMESPACE = "ago.";

function _safe(fn, fallback) {
  try { return fn(); } catch { return fallback; }
}

export const safeStorage = {
  get(key, fallback = null) {
    if (FORBIDDEN_KEYS.test(key)) return fallback;
    return _safe(() => {
      const raw = window.localStorage.getItem(NAMESPACE + key);
      if (raw == null) return fallback;
      try { return JSON.parse(raw); } catch { return raw; }
    }, fallback);
  },

  set(key, value) {
    if (FORBIDDEN_KEYS.test(key)) {
      // eslint-disable-next-line no-console
      console.warn(`[safeStorage] refusing to persist '${key}' — keys matching ${FORBIDDEN_KEYS} must use HttpOnly cookies.`);
      return false;
    }
    return _safe(() => {
      const serialised = typeof value === "string" ? value : JSON.stringify(value);
      window.localStorage.setItem(NAMESPACE + key, serialised);
      return true;
    }, false);
  },

  remove(key) {
    return _safe(() => {
      window.localStorage.removeItem(NAMESPACE + key);
      return true;
    }, false);
  },

  has(key) {
    return _safe(() => window.localStorage.getItem(NAMESPACE + key) != null, false);
  },
};

export default safeStorage;
